import { Phone, Check } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { Reveal, SectionHead } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import CtaBand from "@/components/CtaBand";
import { useQuote } from "@/context/QuoteContext";
import { SERVICES, PHONE_DISPLAY, TEL_LINK, waLink } from "@/constants/site";

const Services = () => {
  const { openQuote } = useQuote();

  return (
    <>
      <section className="bg-asphalt py-20 sm:py-28 grain relative" data-testid="services-page-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <p className="font-mono text-xs tracking-[0.3em] text-emergency uppercase">Services</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase leading-[1.02] text-white">
            One number.<br /><span className="text-emergency">Every roadside problem.</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg text-zinc-400">
            Call {PHONE_DISPLAY} or text us — every service below starts with a fast, honest quote.
          </p>
        </div>
      </section>
      <Marquee />

      <section className="bg-[#F8F9FA] py-20 sm:py-28" data-testid="services-list">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 space-y-8">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id}>
              <article
                className={`group grid overflow-hidden border border-zinc-200 bg-white lg:grid-cols-2 ${
                  i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
                }`}
                data-testid={`service-detail-${s.id}`}
              >
                <div className={`relative overflow-hidden bg-charcoal ${s.fit === "contain" ? "flex items-center justify-center py-6 lg:py-10" : "h-64 lg:h-auto"}`}>
                  <img
                    src={s.image}
                    alt={s.title}
                    className={`transition-transform duration-700 group-hover:scale-105 ${
                      s.fit === "contain" ? "max-h-[420px] w-auto max-w-full object-contain" : "h-full w-full object-cover"
                    }`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 to-transparent" />
                  <span className="absolute top-5 left-5 bg-emergency px-3 py-1.5 font-mono text-[10px] font-bold tracking-[0.2em] uppercase text-white">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="flex flex-col justify-center p-7 sm:p-10">
                  <div className="flex items-center gap-3">
                    <s.icon className="h-7 w-7 text-emergency" strokeWidth={1.8} />
                    <h2 className="font-display text-2xl sm:text-3xl font-black uppercase text-zinc-900">{s.title}</h2>
                  </div>
                  <p className="mt-4 text-base leading-relaxed text-zinc-600">{s.desc}</p>
                  <ul className="mt-5 space-y-2.5">
                    {s.features.map((f) => (
                      <li key={f} className="flex items-center gap-2.5 text-sm text-zinc-700">
                        <Check className="h-4 w-4 text-dispatch shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-7 flex flex-col sm:flex-row gap-3">
                    <a
                      href={TEL_LINK}
                      data-testid={`service-${s.id}-call-button`}
                      className="inline-flex items-center justify-center gap-2 bg-emergency px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-emergency-dark"
                    >
                      <Phone className="h-4 w-4" /> Call {PHONE_DISPLAY}
                    </a>
                    <a
                      href={waLink(`Hi Paliway Towing! I need ${s.title} in Tarrant County. My location is: `)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`service-${s.id}-whatsapp-button`}
                      className="inline-flex items-center justify-center gap-2 bg-dispatch px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-dispatch-dark"
                    >
                      <FaWhatsapp className="h-4 w-4" /> Text / WhatsApp
                    </a>
                    <button
                      onClick={() => openQuote({ label: s.title, msg: `Hi Paliway Towing! I need ${s.title} in Tarrant County. My location is: ` })}
                      data-testid={`service-${s.id}-quote-button`}
                      className="inline-flex items-center justify-center gap-2 border border-zinc-300 px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-zinc-800 transition-colors hover:border-emergency hover:text-emergency"
                    >
                      Get a Quote
                    </button>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <CtaBand />
    </>
  );
};

export default Services;
